import React, { useState, useEffect, useCallback } from "react";
import { Box, Paper, Grid, Typography, Divider, Container } from "@mui/material";
import { useLocation } from "react-router-dom";
import {
  BadgeOutlined as ProfileIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  ContactMailOutlined as ContactIcon,
  MonetizationOnOutlined as CostIcon,
  ScheduleOutlined as ScheduleIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import BigNumber from "bignumber.js";

import cinstance from "../../Service/usercinstance";
import web3 from "../../Service/web3";
import { Path, getNetworkCurrency } from "../../../static/Constants";
import { networkID } from "../../../static/utils";
import { colors, borderRadiusPx, customShadows } from "../../../theme";
import { NavBar } from "../../Components/home";
import {
  CommonButton,
  CommonTextField,
  CommonDateTimePicker,
} from "../../Components/ui";
import Loaders from "../../Components/Loaders";
import FailedPopup from "../../Components/FailedPopup";
import SuccessPopup from "../../Components/SuccessPopup";

const getMaxDob = () => new Date(moment().subtract(18, "years"));

const computeRegistrationFee = (minValue, maxValue, registerFeePercent) => {
  if (minValue === "" || maxValue === "" || registerFeePercent === "") {
    return null;
  }
  try {
    const minFeesP = web3.utils.toWei(`${minValue}`, "ether");
    const maxFeesP = web3.utils.toWei(`${maxValue}`, "ether");
    const med = BigNumber(maxFeesP).plus(BigNumber(minFeesP)).dividedBy(2);
    const regfee = BigNumber(med)
      .multipliedBy(registerFeePercent)
      .dividedBy(100)
      .toFixed(0);
    return web3.utils.fromWei(`${regfee}`, "ether");
  } catch {
    return null;
  }
};

const shortenAddress = (value) => {
  if (!value || value.length < 12) return value || "";
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
};

const SectionHeader = ({ icon, bgcolor, color, title }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        borderRadius: borderRadiusPx.md,
        bgcolor,
        color,
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        fontWeight: 700,
        color: colors.slate[900],
        fontSize: "16px",
        letterSpacing: "-0.01em",
      }}
    >
      {title}
    </Typography>
  </Box>
);

const InfoCallout = ({ icon, title, children }) => (
  <Box
    sx={{
      mt: 1.5,
      py: 1,
      px: 1.5,
      borderRadius: borderRadiusPx.lg,
      bgcolor: colors.slate[50],
      border: `1px solid ${colors.dashboard.cardBorder}`,
      display: "flex",
      alignItems: "center",
      gap: 1.25,
    }}
  >
    <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
      {icon}
    </Box>
    <Typography sx={{ fontSize: "12.5px", color: colors.slate[600], lineHeight: 1.4 }}>
      <strong style={{ color: colors.slate[800] }}>{title}</strong> {children}
    </Typography>
  </Box>
);

const RegisterAsorganisation = () => {
  const location = useLocation();

  const [name, setFirstname] = useState("");
  const [phoneno, setphoneno] = useState("");
  const [resiAddress, setResiaddress] = useState("");
  const [dob, setDob] = useState(null);
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [refaddress, setrefAddress] = useState("");
  const [loading, setloading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [fees, setFees] = useState(1.25);
  const [minfees, setminFees] = useState(10);
  const [maxfees, setmaxFees] = useState(15);
  const [registerFeePercent, setRegisterFeePercent] = useState("");
  const [showFail, setShowFail] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState(() =>
    getNetworkCurrency(networkID)
  );

  const applyFeeFromLimits = useCallback(
    (minValue, maxValue, percent = registerFeePercent) => {
      const nextFee = computeRegistrationFee(minValue, maxValue, percent);
      if (nextFee != null) {
        setFees(nextFee);
      }
    },
    [registerFeePercent]
  );

  const handleMinFeesChange = (event) => {
    const mn = Number(event.target.value);
    const mx = Math.ceil(Number(event.target.value) + Number(event.target.value) / 2);
    setminFees(mn);
    setmaxFees(mx);
    applyFeeFromLimits(mn, mx);
  };

  const handleMaxFeesChange = (event) => {
    const mx = event.target.value;
    setmaxFees(mx);
    applyFeeFromLimits(minfees, mx);
  };

  const onSendmsg = async (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
      event.stopPropagation();
    }

    setValidated(true);

    let isValid = true;
    if (!name || name.trim() === "") isValid = false;
    if (!address || address.trim() === "") isValid = false;
    if (minfees === "" || Number(minfees) <= 0) isValid = false;
    if (maxfees === "" || Number(maxfees) <= 0) isValid = false;
    if (Number(maxfees) < Number(minfees)) isValid = false;
    if (!email || email.trim() === "" || !email.includes("@")) isValid = false;
    if (!phoneno || String(phoneno).trim() === "") isValid = false;
    if (!resiAddress || resiAddress.trim() === "") isValid = false;
    if (!dob) isValid = false;

    if (!isValid) {
      return;
    }

    setloading(true);

    try {
      const accounts = await web3.eth.getAccounts();
      if (!accounts || accounts.length === 0) {
        toast.error("No active Web3 wallet connected.");
        setloading(false);
        return;
      }

      const dt = moment(dob).unix();
      const currentDT = moment().unix();
      const feeP = web3.utils.toWei(`${fees}`, "ether");
      const minFeesP = web3.utils.toWei(`${minfees}`, "ether");
      const maxFeesP = web3.utils.toWei(`${maxfees}`, "ether");
      const currentNetworkID = await web3.eth.net.getId();
      const tradd =
        refaddress !== ""
          ? refaddress
          : "0x0000000000000000000000000000000000000000";

      if (currentDT < dt) {
        toast.error("Please enter a valid date of birth.");
        setloading(false);
        return;
      }
      if (Number(minFeesP) <= 0) {
        toast.error("Minimum fee should be greater than 0.");
        setloading(false);
        return;
      }
      if (Number(maxFeesP) <= 0) {
        toast.error("Maximum fee should be greater than 0.");
        setloading(false);
        return;
      }
      if (currentNetworkID !== networkID) {
        toast.error("You are not connected to the correct network.");
        setloading(false);
        return;
      }

      await cinstance.methods
        .addOrganisation(
          address,
          tradd,
          name,
          phoneno,
          dt,
          email,
          resiAddress,
          minFeesP,
          maxFeesP
        )
        .send({
          from: accounts[0],
          value: feeP,
        });

      setloading(false);
      setShowSuccess(true);
    } catch (err) {
      console.error("Failed creator registration:", err);
      setShowFail(true);
      setloading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      const queryParams = new URLSearchParams(location.search);
      const refAddressFromURL = queryParams.get("ref");
      if (refAddressFromURL && isMounted) {
        setrefAddress(refAddressFromURL);
      }

      try {
        if (web3?.eth?.net?.getId) {
          const currentNetId = await web3.eth.net.getId();
          if (isMounted) {
            setCurrencySymbol(getNetworkCurrency(currentNetId));
          }
        }
      } catch (err) {
        console.warn("Could not determine dynamic network currency:", err);
      }

      try {
        const response = await cinstance.methods.bregisterFee().call();
        if (isMounted) {
          setRegisterFeePercent(response);
          const nextFee = computeRegistrationFee(minfees, maxfees, response);
          if (nextFee != null) {
            setFees(nextFee);
          }
        }
      } catch (err) {
        console.warn("Could not fetch creator registration fee:", err);
      }

      if (window.ethereum && web3) {
        window.web3 = web3;
        try {
          const account = await web3.eth.getAccounts();
          if (isMounted && account && account.length > 0) {
            setAddress(account[0]);
          }
        } catch (err) {
          console.warn("Could not read connected wallet:", err);
        }
      }
    }

    fetchData();

    const handleAccountsChanged = async () => {
      try {
        const account = await web3.eth.getAccounts();
        setAddress(account[0] || "");
      } catch {
        setAddress("");
      }
    };

    if (window.ethereum?.on) {
      window.ethereum.on("accountsChanged", handleAccountsChanged);
    }

    return () => {
      isMounted = false;
      if (window.ethereum?.removeListener) {
        window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
      }
    };
  }, [location.search]);

  const isNameInvalid = validated && (!name || name.trim() === "");
  const isWalletInvalid = validated && (!address || address.trim() === "");
  const isMinInvalid = validated && (minfees === "" || Number(minfees) <= 0);
  const isMaxInvalid =
    validated &&
    (maxfees === "" || Number(maxfees) <= 0 || Number(maxfees) < Number(minfees));
  const isEmailInvalid =
    validated && (!email || email.trim() === "" || !email.includes("@"));
  const isPhoneInvalid = validated && (!phoneno || String(phoneno).trim() === "");
  const isAddressInvalid = validated && (!resiAddress || resiAddress.trim() === "");
  const isDobInvalid = validated && !dob;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: colors.dashboard.canvas,
        pt: { xs: 10, md: 12 },
        pb: 6,
      }}
    >
      <NavBar />
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar />
      {loading && <Loaders />}

      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: "24px", sm: "28px" },
              color: colors.slate[900],
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Register as Creator
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: "14px", color: colors.slate[500] }}>
            Create your organisation profile, set prize bounds, and activate on-chain creator access
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3.5 },
            borderRadius: borderRadiusPx.xl,
            border: `1px solid ${colors.dashboard.cardBorder}`,
            bgcolor: colors.background.paper,
            boxShadow: customShadows.cardFlat,
          }}
        >
          <Box component="form" noValidate onSubmit={onSendmsg} sx={{ width: "100%" }}>
            <Box sx={{ mb: 2.5 }}>
              <SectionHeader
                icon={<ProfileIcon sx={{ fontSize: 18 }} />}
                bgcolor="rgba(37, 99, 235, 0.1)"
                color={colors.primary.main}
                title="Creator Identity"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <CommonTextField
                    label="Full Name"
                    required
                    placeholder="Your full name"
                    value={name}
                    onChange={(event) => setFirstname(event.target.value)}
                    error={isNameInvalid}
                    helperText={isNameInvalid ? "Enter your name" : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CommonTextField
                    label="Wallet Address"
                    required
                    value={address || ""}
                    readOnly
                    error={isWalletInvalid}
                    helperText={
                      isWalletInvalid
                        ? "Connect a Web3 wallet to continue"
                        : address
                        ? `Connected wallet ${shortenAddress(address)}`
                        : "Connect a wallet to auto-fill this field"
                    }
                    startAdornment={
                      <WalletIcon sx={{ fontSize: 18, color: colors.slate[400] }} />
                    }
                  />
                </Grid>
              </Grid>
              <InfoCallout
                icon={<InfoIcon sx={{ fontSize: 16, color: colors.primary.main }} />}
                title="On-chain profile:"
              >
                Your connected wallet becomes the creator account used to deploy lotteries and gift cards.
              </InfoCallout>
            </Box>

            <Divider sx={{ my: 2.5, borderColor: colors.dashboard.divider }} />

            <Box sx={{ mb: 2.5 }}>
              <SectionHeader
                icon={<CostIcon sx={{ fontSize: 18 }} />}
                bgcolor="rgba(16, 185, 129, 0.1)"
                color={colors.emerald[600]}
                title="Prize Limits"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <CommonTextField
                    label="Minimum Winning Amount"
                    required
                    type="number"
                    placeholder="0.00"
                    value={minfees}
                    endAdornment={currencySymbol}
                    onChange={handleMinFeesChange}
                    error={isMinInvalid}
                    helperText={
                      isMinInvalid
                        ? "Minimum winning amount must be greater than 0"
                        : "Lowest prize you can set when creating games"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CommonTextField
                    label="Maximum Winning Amount"
                    required
                    type="number"
                    placeholder="0.00"
                    value={maxfees}
                    endAdornment={currencySymbol}
                    onChange={handleMaxFeesChange}
                    error={isMaxInvalid}
                    helperText={
                      isMaxInvalid
                        ? "Must be greater than 0 and at least the minimum"
                        : "Highest prize you can set when creating games"
                    }
                  />
                </Grid>
              </Grid>
              <InfoCallout
                icon={<InfoIcon sx={{ fontSize: 16, color: colors.emerald[600] }} />}
                title="Fee basis:"
              >
                Registration fee is calculated from the midpoint of these prize bounds
                {registerFeePercent ? ` at ${registerFeePercent}%` : ""}.
              </InfoCallout>
            </Box>

            <Divider sx={{ my: 2.5, borderColor: colors.dashboard.divider }} />

            <Box sx={{ mb: 2.5 }}>
              <SectionHeader
                icon={<ContactIcon sx={{ fontSize: 18 }} />}
                bgcolor="rgba(168, 85, 247, 0.1)"
                color={colors.purple?.[600] || "#9333ea"}
                title="Contact & Referral"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <CommonTextField
                    label="Email"
                    required
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    error={isEmailInvalid}
                    helperText={isEmailInvalid ? "Enter a valid email" : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CommonTextField
                    label="Phone Number"
                    required
                    type="tel"
                    placeholder="Phone number"
                    value={phoneno}
                    onChange={(event) => setphoneno(event.target.value)}
                    error={isPhoneInvalid}
                    helperText={isPhoneInvalid ? "Enter a phone number" : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CommonTextField
                    label="Referral Address"
                    placeholder="Optional referral wallet"
                    value={refaddress}
                    onChange={(event) => setrefAddress(event.target.value)}
                    helperText="Leave empty if you were not referred"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CommonDateTimePicker
                    label="Date of Birth"
                    required
                    value={dob}
                    onChange={(value) => setDob(value || null)}
                    format="dd-MM-y"
                    disableClock
                    maxDate={getMaxDob()}
                    dayPlaceholder="DD"
                    monthPlaceholder="MM"
                    yearPlaceholder="YYYY"
                    calendarProps={{
                      defaultView: "decade",
                      minDetail: "decade",
                    }}
                    error={isDobInvalid}
                    helperText={
                      isDobInvalid
                        ? "Enter your date of birth"
                        : "Must be at least 18 years of age"
                    }
                    calendarIcon={
                      <ScheduleIcon sx={{ fontSize: 18, color: colors.slate[400] }} />
                    }
                    clearIcon={null}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CommonTextField
                    label="Resident Address"
                    required
                    placeholder="Street, city, country"
                    value={resiAddress}
                    onChange={(event) => setResiaddress(event.target.value)}
                    error={isAddressInvalid}
                    helperText={
                      isAddressInvalid ? "Enter your resident address" : undefined
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: borderRadiusPx.xl,
                bgcolor: colors.slate[50],
                border: `1px solid ${colors.dashboard.cardBorder}`,
                mt: 2.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.blue[50],
                      color: colors.primary.main,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CostIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: colors.slate[500],
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      Registration Fee
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "20px",
                        fontWeight: 800,
                        color: colors.slate[900],
                        lineHeight: 1.2,
                      }}
                    >
                      {Number(fees || 0).toLocaleString(undefined, {
                        maximumFractionDigits: 6,
                      })}{" "}
                      {currencySymbol}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Typography sx={{ fontSize: "12px", color: colors.slate[600] }}>
                    Paid from your connected wallet when you sign up
                  </Typography>
                  {registerFeePercent !== "" && registerFeePercent != null && (
                    <Typography sx={{ fontSize: "12px", color: colors.slate[600], mt: 0.25 }}>
                      Protocol registration rate: <strong>{registerFeePercent}%</strong>
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                <CommonButton
                  type="submit"
                  variant="contained"
                  size="large"
                  loading={loading}
                  disabled={loading}
                  startIcon={<ProfileIcon />}
                  sx={{
                    minWidth: { xs: "100%", sm: "220px" },
                  }}
                >
                  Register as Creator
                </CommonButton>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>

      <SuccessPopup
        show={showSuccess}
        setShowModal={(e) => setShowSuccess(e)}
        message="Account activated successfully!"
        redirectPath={Path.org}
        btnName="Go to creator dashboard"
      />
      <FailedPopup show={showFail} setShowModal={(e) => setShowFail(e)} />
    </Box>
  );
};

export default RegisterAsorganisation;
